/**
 * You have used all the sites your plan includes.
 *
 * This was the loudest thing left in the application. A 1.5px chartreuse bar
 * across the top of a cream card, a monospace uppercase "DOMAIN CAPACITY"
 * eyebrow, a 48px serif headline, three benefit tiles each with a chartreuse
 * icon square, a chartreuse tick, and a purple-shadowed pill. All of it drawn
 * at the moment somebody is being asked to pay.
 *
 * Three things were wrong beyond the palette.
 *
 * It said the number twice: a 60px serif figure reading "5" over the words
 * "domains active", and directly under it "5 of 3 domains used". Two of three
 * is a fraction; three on its own is a fact; both together is the reader doing
 * arithmetic to find out which one they were meant to read. And when somebody
 * is over an old plan's allowance the pair reads as broken: "5", then "5 of 3".
 *
 * The eyebrow named our feature, not their problem, above a heading that then
 * had to ask a question to get back to them.
 *
 * And the three tiles listed what the product does, in our words, none of it
 * about the thing they were just stopped from doing.
 *
 * What is left is the shape every other locked feature in the app already
 * uses: what you have, what it would take, and the two ways out. It does not
 * name a tier, because this modal is not told which plan would fit; that is
 * what the plans page is for and it is one press away.
 */
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { DsIsland } from '@/design-system/island'
import { Button, Dialog } from '@/design-system/primitives'
import { useTrackUpgradeCTA } from '@/hooks/useTrackUpgradeCTA'
/* `ds-unlock__list` is the list every other locked feature uses. */
import '@/design-system/pagedetail.css'

interface DomainLimitModalProps {
  isOpen: boolean
  onClose: () => void
  currentCount: number
  maxCount: number
  reason: string        // e.g., "scan another domain", "track this subdomain"
  triggerSource: string // for CTA tracking: 'ScanTypeModal', 'AddDomainDialog', etc.
}

const WHAT_A_PLAN_ADDS = [
  'Room for the rest of your sites, under one workspace.',
  'We watch the pages that matter and tell you when one changes.',
  'You see which assistants read your site, and which pages they read.',
]

export default function DomainLimitModal({
  isOpen,
  onClose,
  currentCount,
  maxCount,
  reason,
  triggerSource,
}: DomainLimitModalProps) {
  const navigate = useNavigate()
  const { trackCTA } = useTrackUpgradeCTA()

  if (!isOpen) return null

  /*
    Each number said once, and the two states said differently.

    `currentCount` can legitimately exceed `maxCount`: an account that was on a
    larger plan and moved down keeps the sites it has. The old copy printed a
    60px "5" over "domains active" and then "5 of 3 domains used" underneath,
    which is the same fact twice in the ordinary case and reads as an error in
    this one.
  */
  const sites = (n: number) => `${n} ${n === 1 ? 'site' : 'sites'}`
  const held =
    currentCount > maxCount
      ? `This plan covers ${sites(maxCount)} and you have ${currentCount}.`
      : `This plan covers ${sites(maxCount)}, and you are using all of them.`

  const handleUpgrade = () => {
    trackCTA({
      componentName: triggerSource,
      action: 'click',
      metadata: { reason, currentCount, maxCount },
    })
    onClose()
    navigate('/settings/billing?section=plans#subscription-plans')
  }

  const dialog = (
    /*
      The scope travels with it. This portals to <body>, which is outside every
      `.ds` on the page, and the system's rules are all written `.ds .ds-x`: on
      its own out there the backdrop and the card render as unstyled boxes in
      the top left corner. Only looking at it finds this.
    */
    <DsIsland panel={false} ground={false}>
    <Dialog
      open
      onClose={onClose}
      title="Your plan is full"
      lead={`${held} A bigger plan is what it takes to ${reason}.`}
      size="md"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Maybe later
          </Button>
          <Button onClick={handleUpgrade}>See subscription plans</Button>
        </>
      }
    >
      <div className="ds-unlock">
        <ul className="ds-unlock__list">
          {WHAT_A_PLAN_ADDS.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>
    </Dialog>
    </DsIsland>
  )

  /*
    Still portalled to <body>. This opens from inside AddDomainDialog, whose
    own backdrop carries a `backdrop-filter`, and a filtered element becomes
    the containing block for any `position: fixed` descendant, so a dialog
    rendered inside it would be trapped in it rather than covering the window.
  */
  if (typeof document === 'undefined') {
    return dialog
  }

  return createPortal(dialog, document.body)
}
